import DeleteModal from "components/DeleteModal";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteExhibit,
  fetchExhibitByIncidentRegistrationNumber,
} from "services/incidentService";
import { CrimeReference, Exhibit, FILE_URL, Investigation } from "types";
import { deleteIcon, editIcon, returnIcon, viewIcon } from "utils/constant";
import { errorHandler, errorHandlerOnForm } from "utils/helper";
import ExhibitDetail from "./modals/ExhibitDetail";
import ExhibitForm from "./modals/ExhibitForm";
import DocumentViewerModal from "components/DocumentViewer";

// Reusable Action Button Component
const ActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  className?: string;
}> = ({ onClick, children, variant = "outline", className = "" }) => {
  const baseClasses =
    "px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2";
  const variants = {
    primary: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 shadow-sm",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${className}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

type Props = {
  investigation: Investigation;
  incident?: CrimeReference;
  canManageIncidentDetail: boolean;
};

const ExhibitsTab: React.FC<Props> = ({
  investigation,
  incident,
  canManageIncidentDetail = false,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [exhibits, setExhibits] = useState<Exhibit[]>([]);
  const [action, setAction] = useState<"Add" | "Edit">("Add");
  const [isReturn, setIsReturn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOpenForm, setIsOpenForm] = React.useState(false);
  const [isOpenDetail, setIsOpenDetail] = React.useState(false);
  const [isOpenDelete, setIsOpenDelete] = React.useState(false);
  const [selectedExhibit, setSelectedEvidence] = React.useState<Exhibit>();

  const fetchExhibitData = async () => {
    setLoading(true);
    fetchExhibitByIncidentRegistrationNumber(
      incident?.Incident_Registration_number || "",
    )
      .then((data) =>
        setExhibits(
          data?.filter(
            (e) =>
              e?.Incident_Registration_number ===
              incident?.Incident_Registration_number,
          ) || [],
        ),
      )
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchExhibitData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">📦</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t("exhibit_files")}
              </h1>
              <p className="text-gray-600 mt-1">
                {t("manage_exhibit_files_for_incident")}
              </p>
            </div>
          </div>

          {canManageIncidentDetail && incident?.status !== "forwarded" && (
            <ActionButton
              onClick={() => {
                setIsOpenForm(true);
                setAction("Add");
              }}
            >
              ➕ {t("add_exhibits")}
            </ActionButton>
          )}
        </div>
      </div>

      {/* Exhibits List */}
      {exhibits.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center text-gray-500">
            {t("no_exhibits_found")}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {exhibits.map((exhibit, index) => (
            <div
              key={exhibit?.uu_id ?? index}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <span className="text-gray-600">📦</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {exhibit?.tool_type ?? t("unknown_exhibit_title")}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {t("collected_by")}: {exhibit?.collected_by ?? "-"}
                    </p>

                    {exhibit?.attachments &&
                      exhibit?.attachments.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {exhibit.attachments.map((file, fileIndex) => (
                            <a
                              href={file.file}
                              download={file.file}
                              key={file?.id ?? fileIndex}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                            >
                              📎 {t("exhibit_file_label")} {fileIndex + 1}
                            </a>
                          ))}
                        </div>
                      )}
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-4 md:mt-0">
                  <button
                    type="button"
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                    title={t("view_details_button")}
                    onClick={() => {
                      setSelectedEvidence(exhibit);
                      setIsOpenDetail(true);
                    }}
                  >
                    {viewIcon}
                  </button>

                  {!exhibit?.is_exibit_retuned && (
                    <button
                      type="button"
                      className="p-2 text-gray-600 hover:text-green-600 hover:bg-gray-50 rounded-lg transition-colors"
                      title={t("return_exhibit_action")}
                      onClick={() => {
                        setAction("Edit");
                        setIsReturn(true);
                        setSelectedEvidence(exhibit);
                        setIsOpenForm(true);
                      }}
                    >
                      {returnIcon}
                    </button>
                  )}

                  {canManageIncidentDetail &&
                    incident?.status !== "forwarded" && (
                      <>
                        <button
                          type="button"
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t("edit_exhibit")}
                          onClick={() => {
                            setAction("Edit");
                            setSelectedEvidence(exhibit);
                            setIsOpenForm(true);
                          }}
                        >
                          {editIcon}
                        </button>
                        <button
                          type="button"
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t("delete_exhibit")}
                          onClick={() => {
                            setSelectedEvidence(exhibit);
                            setIsOpenDelete(true);
                          }}
                        >
                          {deleteIcon}
                        </button>
                      </>
                    )}
                </div>
              </div>

              {/* Status indicators */}
              <div className="mt-4 space-y-3">
                {exhibit?.is_exibit_retuned && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                    <span className="text-green-700 font-medium">
                      {t("exhibit_status_returned")}
                    </span>
                    <span className="text-sm text-green-600">
                      {t("exhibit_to_label")}: {exhibit?.exhibit_returned_to} •{" "}
                      {t("date")}:{" "}
                      {exhibit?.exhibit_return_date
                        ? new Date(
                            exhibit.exhibit_return_date,
                          ).toLocaleDateString()
                        : "-"}
                    </span>
                  </div>
                )}

                {exhibit?.is_exbit_sold && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <span className="text-yellow-700 font-medium">
                      {t("exhibit_status_sold")}
                    </span>
                    <span className="text-sm text-yellow-600">
                      {t("exhibit_amount_label")}:{" "}
                      {exhibit?.exhibit_sale_amount} • {t("date")}:{" "}
                      {exhibit?.exhibit_sale_date
                        ? new Date(
                            exhibit.exhibit_sale_date,
                          ).toLocaleDateString()
                        : "-"}
                    </span>
                  </div>
                )}
              </div>

              {/* Decision files */}
              <div className="mt-4 space-y-2">
                <div className="mt-4">
                  <DocumentViewerModal
                    documents={(exhibit?.documents || []) as any}
                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.EXIBIT}`}
                    layout="list"
                    hideEmptyFile={true}
                  />
                </div>

                {exhibit?.court_decision_file && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700">
                      {t("exhibit_court_decision")}:
                    </span>
                    <a
                      href={String(exhibit.court_decision_file)}
                      download
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                    >
                      {t("download")}
                    </a>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {isOpenForm && (
        <ExhibitForm
          action={action}
          investigation={investigation}
          incident={incident}
          exhibit={action === "Edit" ? selectedExhibit : undefined}
          isOpen={isOpenForm}
          isReturn={isReturn}
          onClose={(refetch) => {
            setIsOpenForm(false);
            if (refetch) fetchExhibitData();
          }}
        />
      )}
      {isOpenDetail && (
        <ExhibitDetail
          exhibit={selectedExhibit}
          isOpen={isOpenDetail}
          onClose={() => setIsOpenDetail(false)}
        />
      )}
      {selectedExhibit && (
        <DeleteModal
          isOpen={isOpenDelete}
          onConfirm={() => {
            deleteExhibit(selectedExhibit)
              .then(() => {
                showTooltip(t("successfully_deleted"), "success");
                fetchExhibitData();
              })
              .catch((error) => {
                showTooltip(
                  error?.response?.data?.detail ||
                    errorHandler(error) ||
                    t("something_went_wrong_try_again"),
                  "error",
                );
              });
          }}
          onCancel={() => {
            setIsOpenDelete(false);
          }}
          title={t("delete_exhibit")}
          message={t("confirm_delete_exhibit")}
        />
      )}
    </div>
  );
};

export default ExhibitsTab;
